import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';

interface Board {
  id: number;
  name: string;
}

interface Standard {
  id: number;
  name: string;
}

interface Subject {
  id: number;
  name: string;
}

interface Chapter {
  id: number;
  name: string;
}

interface Topic {
  id: number;
  name: string;
}

interface Subtopic {
  id: number;
  name: string;
}

interface Stream {
  id: number;
  name: string;
}

interface Medium {
  id: number;
  code: string;
  name: string;
}

interface QuestionFormData {
  board_id: string;
  standard_id: string;
  medium_id: string;
  stream_id: string;
  subject_id: string;
  chapter_id: string;
  topic_id: string;
  subtopic_id: string;
  page_number: string;
  question: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  question_type: 'single' | 'multiple';
  correct_option: string;
  correct_options: string[];
  difficulty_level: string;
  marks: string;
  explanation: string;
  language: string;
}

const QuestionEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id; // true only if id exists (from /questions/:id/edit)

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [originalStatus, setOriginalStatus] = useState<string>('');

  // Master data
  const [boards, setBoards] = useState<Board[]>([]);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [mediums, setMediums] = useState<Medium[]>([]);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [subtopics, setSubtopics] = useState<Subtopic[]>([]);

  // Form data
  const [formData, setFormData] = useState<QuestionFormData>({
    board_id: '',
    standard_id: '',
    medium_id: '',
    stream_id: '',
    subject_id: '',
    chapter_id: '',
    topic_id: '',
    subtopic_id: '',
    page_number: '',
    question: '',
    option1: '',
    option2: '',
    option3: '',
    option4: '',
    question_type: 'single',
    correct_option: '1',
    correct_options: ['1'],
    difficulty_level: 'medium',
    marks: '1',
    explanation: '',
    language: 'English',
  });

  useEffect(() => {
    loadMasterData();
    if (isEditMode) {
      loadQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMasterData = async () => {
    try {
      const [boardsResponse, mediumsResponse] = await Promise.all([
        api.master.getBoards(),
        api.master.getMediums(),
      ]);
      setBoards(boardsResponse.data.boards || []);
      setMediums(mediumsResponse.data.mediums || []);
    } catch (err) {
      console.error('Failed to load master data:', err);
    }
  };

  const loadStandards = async (boardId: string) => {
    if (!boardId) {
      setStandards([]);
      return;
    }
    try {
      const response = await api.master.getStandards(parseInt(boardId));
      setStandards(response.data.standards || []);
    } catch (err) {
      console.error('Failed to load standards:', err);
    }
  };

  const loadStreams = async (standardId: string) => {
    if (!standardId) {
      setStreams([]);
      return;
    }
    try {
      const response = await api.master.getStreams(parseInt(standardId));
      setStreams(response.data.streams || []);
    } catch (err) {
      console.error('Failed to load streams:', err);
    }
  };

  const loadSubjects = async (boardId: string, standardId: string, mediumId?: string) => {
    if (!boardId || !standardId) {
      setSubjects([]);
      return;
    }
    try {
      const response = await api.master.getSubjects(
        parseInt(boardId),
        parseInt(standardId),
        mediumId ? parseInt(mediumId) : undefined
      );
      setSubjects(response.data.subjects || []);
    } catch (err) {
      console.error('Failed to load subjects:', err);
    }
  };

  const loadChapters = async (subjectId: string) => {
    if (!subjectId) {
      setChapters([]);
      return;
    }
    try {
      const response = await api.master.getChapters(parseInt(subjectId));
      setChapters(response.data.chapters || []);
    } catch (err) {
      console.error('Failed to load chapters:', err);
    }
  };

  const loadTopics = async (chapterId: string) => {
    if (!chapterId) {
      setTopics([]);
      return;
    }
    try {
      const response = await api.master.getTopics(parseInt(chapterId));
      setTopics(response.data.topics || []);
    } catch (err) {
      console.error('Failed to load topics:', err);
    }
  };

  const loadSubtopics = async (topicId: string) => {
    if (!topicId) {
      setSubtopics([]);
      return;
    }
    try {
      const response = await api.master.getSubtopics(parseInt(topicId));
      setSubtopics(response.data.subtopics || []);
    } catch (err) {
      console.error('Failed to load subtopics:', err);
      setSubtopics([]);
    }
  };

  const loadQuestion = async () => {
    try {
      setLoading(true);
      const response = await api.questions.getById(parseInt(id!));
      const question = response.data.question;

      // Parse correct_options if it's a string
      const correctOptions = typeof question.correct_options === 'string'
        ? JSON.parse(question.correct_options)
        : question.correct_options || ['1'];

      // Save original status
      setOriginalStatus(question.status || '');

      // Set form data from loaded question
      setFormData({
        board_id: question.board_id?.toString() || '',
        standard_id: question.standard_id?.toString() || '',
        medium_id: question.medium_id?.toString() || '',
        stream_id: question.stream_id?.toString() || '',
        subject_id: question.subject_id?.toString() || '',
        chapter_id: question.chapter_id?.toString() || '',
        topic_id: question.topic_id?.toString() || '',
        subtopic_id: question.subtopic_id?.toString() || '',
        page_number: question.page_number?.toString() || '',
        question: question.question || '',
        option1: question.option1 || '',
        option2: question.option2 || '',
        option3: question.option3 || '',
        option4: question.option4 || '',
        question_type: question.question_type || 'single',
        correct_option: question.correct_option?.toString() || '1',
        correct_options: correctOptions,
        difficulty_level: question.difficulty_level || 'medium',
        marks: question.marks?.toString() || '1',
        explanation: question.explanation || '',
        language: question.language || 'English',
      });

      // Load cascading data
      if (question.board_id) {
        await loadStandards(question.board_id.toString());
      }
      if (question.standard_id) {
        await loadStreams(question.standard_id.toString());
      }
      if (question.board_id && question.standard_id) {
        await loadSubjects(
          question.board_id.toString(),
          question.standard_id.toString(),
          question.medium_id?.toString()
        );
      }
      if (question.subject_id) {
        await loadChapters(question.subject_id.toString());
      }
      if (question.chapter_id) {
        await loadTopics(question.chapter_id.toString());
      }
      if (question.topic_id) {
        await loadSubtopics(question.topic_id.toString());
      }

      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load question');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof QuestionFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleBoardChange = (value: string) => {
    handleChange('board_id', value);
    handleChange('medium_id', '');
    handleChange('standard_id', '');
    handleChange('stream_id', '');
    handleChange('subject_id', '');
    handleChange('chapter_id', '');
    handleChange('topic_id', '');
    handleChange('subtopic_id', '');
    loadStandards(value);
  };

  const handleMediumChange = (value: string) => {
    handleChange('medium_id', value);
    handleChange('subject_id', '');
    handleChange('chapter_id', '');
    handleChange('topic_id', '');
    handleChange('subtopic_id', '');
    if (formData.board_id && formData.standard_id) {
      loadSubjects(formData.board_id, formData.standard_id, value);
    }
  };

  const handleStandardChange = (value: string) => {
    handleChange('standard_id', value);
    handleChange('stream_id', '');
    handleChange('subject_id', '');
    handleChange('chapter_id', '');
    handleChange('topic_id', '');
    handleChange('subtopic_id', '');
    loadStreams(value);
    if (formData.board_id && formData.medium_id) {
      loadSubjects(formData.board_id, value, formData.medium_id);
    }
  };

  const handleSubjectChange = (value: string) => {
    handleChange('subject_id', value);
    handleChange('chapter_id', '');
    handleChange('topic_id', '');
    handleChange('subtopic_id', '');
    loadChapters(value);
  };

  const handleChapterChange = (value: string) => {
    handleChange('chapter_id', value);
    handleChange('topic_id', '');
    handleChange('subtopic_id', '');
    loadTopics(value);
  };

  const handleTopicChange = (value: string) => {
    handleChange('topic_id', value);
    handleChange('subtopic_id', '');
    loadSubtopics(value);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.board_id) errors.board_id = 'Board is required';
    if (!formData.standard_id) errors.standard_id = 'Standard is required';
    if (!formData.medium_id) errors.medium_id = 'Medium is required';
    if (!formData.subject_id) errors.subject_id = 'Subject is required';
    if (!formData.chapter_id) errors.chapter_id = 'Chapter is required';
    if (!formData.topic_id) errors.topic_id = 'Topic is required';
    if (!formData.question.trim()) errors.question = 'Question is required';
    if (!formData.option1.trim()) errors.option1 = 'Option 1 is required';
    if (!formData.option2.trim()) errors.option2 = 'Option 2 is required';
    if (!formData.option3.trim()) errors.option3 = 'Option 3 is required';
    if (!formData.option4.trim()) errors.option4 = 'Option 4 is required';

    if (formData.question_type === 'single') {
      if (!formData.correct_option) errors.correct_option = 'Correct option is required';
    } else {
      if (formData.correct_options.length === 0) {
        errors.correct_option = 'At least one correct option is required';
      }
    }
    if (!formData.difficulty_level) errors.difficulty_level = 'Difficulty level is required';
    if (!formData.marks || parseInt(formData.marks) <= 0) {
      errors.marks = 'Valid marks are required';
    }
    if (!formData.language) errors.language = 'Language is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload: any = {
        board_id: parseInt(formData.board_id),
        standard_id: parseInt(formData.standard_id),
        subject_id: parseInt(formData.subject_id),
        chapter_id: parseInt(formData.chapter_id),
        topic_id: parseInt(formData.topic_id),
        question: formData.question,
        option1: formData.option1,
        option2: formData.option2,
        option3: formData.option3,
        option4: formData.option4,
        question_type: formData.question_type,
        correct_option: formData.question_type === 'single' ? parseInt(formData.correct_option) : null,
        correct_options: formData.correct_options,
        correct_options_count: formData.correct_options.length,
        difficulty_level: formData.difficulty_level,
        marks: parseInt(formData.marks),
        language: formData.language,
      };

      if (formData.stream_id) payload.stream_id = parseInt(formData.stream_id);
      if (formData.subtopic_id) payload.subtopic_id = parseInt(formData.subtopic_id);
      if (formData.page_number) payload.page_number = parseInt(formData.page_number);
      if (formData.explanation.trim()) payload.explanation = formData.explanation;

      if (isEditMode) {
        await api.questions.update(parseInt(id!), payload);
      } else {
        await api.questions.create(payload);
      }

      navigate('/questions');
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} question`);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitAndReview = async () => {
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload: any = {
        board_id: parseInt(formData.board_id),
        standard_id: parseInt(formData.standard_id),
        subject_id: parseInt(formData.subject_id),
        chapter_id: parseInt(formData.chapter_id),
        topic_id: parseInt(formData.topic_id),
        question: formData.question,
        option1: formData.option1,
        option2: formData.option2,
        option3: formData.option3,
        option4: formData.option4,
        question_type: formData.question_type,
        correct_option: formData.question_type === 'single' ? parseInt(formData.correct_option) : null,
        correct_options: formData.correct_options,
        correct_options_count: formData.correct_options.length,
        difficulty_level: formData.difficulty_level,
        marks: parseInt(formData.marks),
        language: formData.language,
      };

      if (formData.stream_id) payload.stream_id = parseInt(formData.stream_id);
      if (formData.subtopic_id) payload.subtopic_id = parseInt(formData.subtopic_id);
      if (formData.page_number) payload.page_number = parseInt(formData.page_number);
      if (formData.explanation.trim()) payload.explanation = formData.explanation;

      // Update the question first
      await api.questions.update(parseInt(id!), payload);

      // Then submit for review
      await api.questions.submitForReview(parseInt(id!));

      navigate('/questions');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update and submit question for review');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/questions')}>
          Back
        </Button>
        <Typography variant="h4" fontWeight="bold">
          {isEditMode ? 'Edit Question' : 'Add New Question'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <form>
          {/* Academic Information */}
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Academic Information
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth required error={!!validationErrors.board_id}>
                <InputLabel>Board</InputLabel>
                <Select
                  value={formData.board_id}
                  label="Board"
                  onChange={(e) => handleBoardChange(e.target.value)}
                >
                  {boards.map((board) => (
                    <MenuItem key={board.id} value={board.id}>
                      {board.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {validationErrors.board_id && (
                <Typography variant="caption" color="error">
                  {validationErrors.board_id}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl
                fullWidth
                required
                disabled={!formData.board_id}
                error={!!validationErrors.medium_id}
              >
                <InputLabel>Medium</InputLabel>
                <Select
                  value={formData.medium_id}
                  label="Medium"
                  onChange={(e) => handleMediumChange(e.target.value)}
                >
                  {mediums.map((medium) => (
                    <MenuItem key={medium.id} value={medium.id}>
                      {medium.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {validationErrors.medium_id && (
                <Typography variant="caption" color="error">
                  {validationErrors.medium_id}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl
                fullWidth
                required
                disabled={!formData.board_id}
                error={!!validationErrors.standard_id}
              >
                <InputLabel>Standard</InputLabel>
                <Select
                  value={formData.standard_id}
                  label="Standard"
                  onChange={(e) => handleStandardChange(e.target.value)}
                >
                  {standards.map((standard) => (
                    <MenuItem key={standard.id} value={standard.id}>
                      {standard.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {validationErrors.standard_id && (
                <Typography variant="caption" color="error">
                  {validationErrors.standard_id}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth disabled={!formData.standard_id}>
                <InputLabel>Stream (Optional)</InputLabel>
                <Select
                  value={formData.stream_id}
                  label="Stream (Optional)"
                  onChange={(e) => handleChange('stream_id', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {streams.map((stream) => (
                    <MenuItem key={stream.id} value={stream.id}>
                      {stream.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl
                fullWidth
                required
                disabled={!formData.standard_id || !formData.medium_id}
                error={!!validationErrors.subject_id}
              >
                <InputLabel>Subject</InputLabel>
                <Select
                  value={formData.subject_id}
                  label="Subject"
                  onChange={(e) => handleSubjectChange(e.target.value)}
                >
                  {subjects.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>
                      {subject.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {validationErrors.subject_id && (
                <Typography variant="caption" color="error">
                  {validationErrors.subject_id}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl
                fullWidth
                required
                disabled={!formData.subject_id}
                error={!!validationErrors.chapter_id}
              >
                <InputLabel>Chapter</InputLabel>
                <Select
                  value={formData.chapter_id}
                  label="Chapter"
                  onChange={(e) => handleChapterChange(e.target.value)}
                >
                  {chapters.map((chapter) => (
                    <MenuItem key={chapter.id} value={chapter.id}>
                      {chapter.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {validationErrors.chapter_id && (
                <Typography variant="caption" color="error">
                  {validationErrors.chapter_id}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl
                fullWidth
                required
                disabled={!formData.chapter_id}
                error={!!validationErrors.topic_id}
              >
                <InputLabel>Topic</InputLabel>
                <Select
                  value={formData.topic_id}
                  label="Topic"
                  onChange={(e) => handleTopicChange(e.target.value)}
                >
                  {topics.map((topic) => (
                    <MenuItem key={topic.id} value={topic.id}>
                      {topic.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {validationErrors.topic_id && (
                <Typography variant="caption" color="error">
                  {validationErrors.topic_id}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth disabled={!formData.topic_id || subtopics.length === 0}>
                <InputLabel>Subtopic (Optional)</InputLabel>
                <Select
                  value={formData.subtopic_id}
                  label="Subtopic (Optional)"
                  onChange={(e) => handleChange('subtopic_id', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {subtopics.map((subtopic) => (
                    <MenuItem key={subtopic.id} value={subtopic.id}>
                      {subtopic.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                label="Page Number (Optional)"
                type="number"
                value={formData.page_number}
                onChange={(e) => handleChange('page_number', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Question Content */}
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Question Content
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                required
                label="Question"
                multiline
                rows={4}
                value={formData.question}
                onChange={(e) => handleChange('question', e.target.value)}
                error={!!validationErrors.question}
                helperText={validationErrors.question}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Option 1"
                value={formData.option1}
                onChange={(e) => handleChange('option1', e.target.value)}
                error={!!validationErrors.option1}
                helperText={validationErrors.option1}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Option 2"
                value={formData.option2}
                onChange={(e) => handleChange('option2', e.target.value)}
                error={!!validationErrors.option2}
                helperText={validationErrors.option2}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Option 3"
                value={formData.option3}
                onChange={(e) => handleChange('option3', e.target.value)}
                error={!!validationErrors.option3}
                helperText={validationErrors.option3}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Option 4"
                value={formData.option4}
                onChange={(e) => handleChange('option4', e.target.value)}
                error={!!validationErrors.option4}
                helperText={validationErrors.option4}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Question Type</InputLabel>
                <Select
                  value={formData.question_type}
                  label="Question Type"
                  onChange={(e) => {
                    const newType = e.target.value as 'single' | 'multiple';
                    setFormData({
                      ...formData,
                      question_type: newType,
                      correct_option: newType === 'single' ? '1' : '',
                      correct_options: newType === 'single' ? ['1'] : [],
                    });
                  }}
                >
                  <MenuItem value="single">Single Choice</MenuItem>
                  <MenuItem value="multiple">Multiple Choice</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl component="fieldset" required error={!!validationErrors.correct_option}>
                <FormLabel component="legend">
                  Correct Option{formData.question_type === 'multiple' ? 's' : ''} *
                </FormLabel>

                {formData.question_type === 'single' ? (
                  <RadioGroup
                    row
                    value={formData.correct_option}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        correct_option: value,
                        correct_options: [value],
                      });
                    }}
                  >
                    <FormControlLabel value="1" control={<Radio />} label="Option 1" />
                    <FormControlLabel value="2" control={<Radio />} label="Option 2" />
                    <FormControlLabel value="3" control={<Radio />} label="Option 3" />
                    <FormControlLabel value="4" control={<Radio />} label="Option 4" />
                  </RadioGroup>
                ) : (
                  <FormGroup row>
                    {['1', '2', '3', '4'].map((optionNum) => (
                      <FormControlLabel
                        key={optionNum}
                        control={
                          <Checkbox
                            checked={formData.correct_options.includes(optionNum)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              let newOptions = [...formData.correct_options];

                              if (checked) {
                                if (!newOptions.includes(optionNum)) {
                                  newOptions.push(optionNum);
                                }
                              } else {
                                newOptions = newOptions.filter(opt => opt !== optionNum);
                              }

                              newOptions.sort();
                              setFormData({
                                ...formData,
                                correct_options: newOptions,
                                correct_option: '',
                              });
                            }}
                          />
                        }
                        label={`Option ${optionNum}`}
                      />
                    ))}
                  </FormGroup>
                )}

                {validationErrors.correct_option && (
                  <Typography variant="caption" color="error">
                    {validationErrors.correct_option}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Explanation (Optional)"
                multiline
                rows={3}
                value={formData.explanation}
                onChange={(e) => handleChange('explanation', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Additional Information */}
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Additional Information
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth required error={!!validationErrors.difficulty_level}>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={formData.difficulty_level}
                  label="Difficulty Level"
                  onChange={(e) => handleChange('difficulty_level', e.target.value)}
                >
                  <MenuItem value="easy">Easy</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="hard">Hard</MenuItem>
                </Select>
              </FormControl>
              {validationErrors.difficulty_level && (
                <Typography variant="caption" color="error">
                  {validationErrors.difficulty_level}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                required
                label="Marks"
                type="number"
                value={formData.marks}
                onChange={(e) => handleChange('marks', e.target.value)}
                error={!!validationErrors.marks}
                helperText={validationErrors.marks}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth required error={!!validationErrors.language}>
                <InputLabel>Language</InputLabel>
                <Select
                  value={formData.language}
                  label="Language"
                  onChange={(e) => handleChange('language', e.target.value)}
                >
                  <MenuItem value="English">English</MenuItem>
                  <MenuItem value="Hindi">Hindi</MenuItem>
                  <MenuItem value="Gujarati">Gujarati</MenuItem>
                </Select>
              </FormControl>
              {validationErrors.language && (
                <Typography variant="caption" color="error">
                  {validationErrors.language}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/questions')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Saving...' : isEditMode ? 'Update Question' : 'Create Question'}
            </Button>

            {isEditMode && originalStatus === 'rejected' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<SendIcon />}
                onClick={handleSubmitAndReview}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Update & Submit for Review'}
              </Button>
            )}
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default QuestionEdit;
